import React, { FC } from 'react';
import { ChevronArrowRightIcon } from 'assets/icons';
import { CommunicationsMessage } from '../communications.model';

interface CommunicationInlineCardProps {
  message: CommunicationsMessage;
  onCtaClick?: () => void;
}

/**
 * Row-attached status sentence (matches merchant-app CommInlineCard).
 * Sits under a labeled row; takes only the width its copy needs.
 */
const CommunicationInlineCard: FC<CommunicationInlineCardProps> = ({ message, onCtaClick }) => {
  const backgroundColor = message.color || '#F4F4F4';
  const fontColor = message.fontColor || '#6B7280';
  const [heading, ...bodyLines] = message.lines ?? [];
  const hasAction = Boolean(message.ctaText?.trim());

  if (!heading) return null;

  const handleCtaClick = () => {
    if (!hasAction || !onCtaClick) return;
    onCtaClick();
  };

  const handleCtaKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleCtaClick();
  };

  return (
    <div
      className="mt-3 inline-block max-w-full rounded-md px-3 py-2.5"
      style={{ backgroundColor, color: fontColor }}
      role="status"
      aria-label={heading}>
      <div className="text-sbase font-normal leading-5" style={{ color: fontColor }}>
        {heading}
      </div>
      {bodyLines.map((line, index) => (
        <div
          key={`communications-inline-line-${message.id}-${index}`}
          className="mt-1 text-sbase font-normal leading-5"
          style={{ color: fontColor }}>
          {line}
        </div>
      ))}
      {hasAction ? (
        <button
          type="button"
          id={`communications-inline-cta-${message.id}`}
          className="mt-1.5 flex cursor-pointer items-center gap-0.5 text-sbase font-semibold"
          style={{ color: fontColor }}
          onClick={handleCtaClick}
          onKeyDown={handleCtaKeyDown}
          aria-label={message.ctaText || 'Learn more'}
          tabIndex={0}>
          {message.ctaText}
          <ChevronArrowRightIcon className="path-fill-current h-4 w-4" style={{ color: fontColor }} />
        </button>
      ) : null}
    </div>
  );
};

export default CommunicationInlineCard;
